/**
 * BookmarksPage - Kaydedilen entryleri gösterir
 * NIP-51 bookmark listesi
 */

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Bookmark, Loader2, ArrowLeft } from 'lucide-react'
import { Link, Navigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useBookmarks } from '../hooks/useBookmarks'
import { EntryCard } from '../components/Entry/EntryCard'
import { getNostrPool } from '../lib/nostrPool'
import { DEFAULT_RELAYS } from '../config/constants'
import type { Event } from 'nostr-tools'

interface BookmarkEntry {
  id: string
  content: string
  author_pubkey: string
  author_username: string
  author_avatar_url: string | null
  author_reputation: number
  created_at: string
  topicTitle: string
  upvotes: number
  downvotes: number
}

export function BookmarksPage() {
  const { pubkey } = useAuth()
  const { bookmarks, isLoading: bookmarksLoading } = useBookmarks()
  const [entries, setEntries] = useState<BookmarkEntry[]>([])
  const [isLoadingEntries, setIsLoadingEntries] = useState(false)

  // Redirect if not logged in
  if (!pubkey) {
    return <Navigate to="/login" replace />
  }

  // Fetch entries for bookmarked IDs
  useEffect(() => {
    if (bookmarks.length === 0) {
      setEntries([])
      return
    }

    setIsLoadingEntries(true)

    const fetchBookmarkedEntries = async () => {
      try {
        const pool = getNostrPool()
        const events = await pool.querySync(DEFAULT_RELAYS, {
          ids: bookmarks
        })

        const loadedEntries: BookmarkEntry[] = events.map((event: Event) => {
          // Robust topic title extraction
          const subjectTag = event.tags.find((t: string[]) => t[0] === 'subject')?.[1]
          const hashtagTopic = event.tags.find((t: string[]) => t[0] === 't' && t[1] !== 'mavililer' && t[1] !== 'mavililersözlük' && t[1] !== 'nostr')?.[1]
          const aTagTopic = event.tags.find((t: string[]) => t[0] === 'a')?.[1]?.split(':')[2]

          const topicTitle = (subjectTag || hashtagTopic || aTagTopic || 'Genel').replace(/-/g, ' ')

          return {
            id: event.id,
            content: event.content,
            author_pubkey: event.pubkey,
            author_username: event.pubkey.slice(0, 8) + '...',
            author_avatar_url: null,
            author_reputation: 0,
            created_at: new Date(event.created_at * 1000).toISOString(),
            topicTitle,
            upvotes: 0,
            downvotes: 0
          }
        })

        // Sort by newest first
        loadedEntries.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        setEntries(loadedEntries)
      } catch (error) {
        console.error('Error fetching bookmarked entries:', error)
      } finally {
        setIsLoadingEntries(false)
      }
    }

    fetchBookmarkedEntries()
  }, [bookmarks])

  const isLoading = bookmarksLoading || isLoadingEntries

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Link
          to="/"
          className="p-2 rounded-xl hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-500" />
        </Link>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-mavi-500/10 flex items-center justify-center">
            <Bookmark className="w-6 h-6 text-mavi-500" />
          </div>
          <div>
            <h1 className="text-2xl font-poppins font-bold text-gray-900 dark:text-white">
              Kaydedilenler
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {bookmarks.length} kayıtlı entry
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-mavi-500 mb-4" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Kaydedilenler yükleniyor...
          </p>
        </div>
      ) : entries.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-16 px-8"
        >
          <div className="w-20 h-20 rounded-full bg-surface-100 dark:bg-surface-800 flex items-center justify-center mx-auto mb-6">
            <Bookmark className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="text-lg font-poppins font-semibold text-gray-900 dark:text-white mb-2">
            Henüz kayıtlı entry yok
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            Entry'lerin altındaki kaydet butonuna tıklayarak beğendiğiniz içerikleri buraya kaydedebilirsiniz.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-mavi-500 hover:bg-mavi-600 text-white rounded-xl font-poppins font-medium transition-colors"
          >
            Keşfetmeye Başla
          </Link>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-4"
        >
          {entries.map((entry, index) => (
            <motion.div
              key={entry.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <EntryCard
                entry={entry}
                showPermalink
              />
            </motion.div>
          ))}
        </motion.div>
      )}
    </div>
  )
}
